import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Box, TextField, Typography } from "@mui/material";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import MyTaxModifiedStatementCheck from "my-tax/components/modified-statement-check/modified-statement-check";
import MyTaxMyPersonalTaxCreditApplicationPrint from "my-tax/components/my-personal-tax-credit-application-print/my-personal-tax-credit-application-print";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import { MY_TAX_GET_PERSONAL_DECLARATION } from "my-tax/graphql";
import { MY_TAX_CREATE_PERSONAL_DECLARATION } from "my-tax/graphql";
import { taxDeclarationPersonalState } from "my-tax/states/tax-declaration-personal-state";
import { OcCard, OcDatePicker, OcDownload } from "oc/components";
import { useOcCurrentUserState, useOcSnackbar } from "oc/hooks";
import { getDateFormatByLocale } from "oc/utils";

type UseFormDataProps = {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationPersonalState);
  const resetStates = useResetRecoilState(taxDeclarationPersonalState);

  const { data, loading, error } = useQuery(MY_TAX_GET_PERSONAL_DECLARATION, {
    fetchPolicy: "cache-and-network",
    skip: !_id,
    variables: {
      _id: Number.parseInt(_id !== undefined ? String(_id) : "0"),
    },
  });

  const result = data?.taxDeclarationPersonal;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_PERSONAL_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationPersonal;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationPersonal",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  useEffect(() => {
    if (!states?.isModifiedDeclaration) {
      handleStateChange({ ...states, noRequestRelief: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.isModifiedDeclaration]);

  const handleStateChange = (newState: any) => {
    setStates(newState);
  };

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleSave,
    saveErrors,
    setSaveErrors,
    handleStateChange,
  };
};

type MyTaxMyPersonalTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxMyPersonalTaxCreditApplication = ({
  fullName,
  taxNumber,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxMyPersonalTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const history = useHistory();

  const title = "Adóelőleg-nyilatkozat a személyi kedvezmény érvényesítéséről ";

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleSave,
    handleStateChange,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const isModified = selected?.isModified;
  if (isModified && !states?.isModifiedDeclaration) {
    handleStateChange({ ...states, isModifiedDeclaration: true });
  }

  const showModifiedButton = states?.state === "E" ? true : false;
  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  const componentRef = useRef(null);

  const { t, i18n } = useTranslation();

  const handleCancel = () => {
    // Only reset states when in edit mode
    if (editable) {
      resetStates();
    }
    setSaveErrors([]);
  };

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handleModChange = ({ key, value }: { key: string; value: any }) => {
    handleChange({ key, value });
  };

  const handlePrint = () => {
    print();
  };

  useEffect(() => {
    return () => {
      handleCancel();
    };
  }, []); // eslint-disable-line

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  type ContractType = {
    code: string;
    company?: {
      name?: string;
      taxNumber?: string;
    };
  };

  let selectedContract = contractsData?.find(
    (item: ContractType) => item.code === contractCode,
  );

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };
  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <MyTaxMyPersonalTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            defectiveIsPermanent={states?.isFinal}
            receiveCrippledBenefit={states?.disabilityPension}
            receiveDefectiveSupport={states?.disabilitySupport}
            crippledDecisionNumber={states?.disabilityPensionNumber}
            defectiveDecisionNumber={states?.disabilitySupportNumber}
            defectiveStart={states?.disabledStateFrom}
            defectiveEnd={states?.disabledStateTo}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            isModifiedApplication={states?.isModifiedDeclaration}
            applyToWithdrawal={states?.applyToWithdrawal}
            noRequestRelief={states?.noRequestRelief}
            applicationDate={dateOfDeclarationString}
          />
        </Box>
        <MyTaxApplicationHeader
          title={title}
          onCancel={handleCancel}
          isMobile={isMobile}
        />
        <Box
          padding="16px"
          display="flex"
          flexDirection="row"
          justifyContent="center"
        >
          <Box
            width="260px"
            style={{
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              label="Tájékoztató"
              iconType="document"
              actionIconType="url"
              href={"#/my-hr/my-personal-tax-credit-information"}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}{" "}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box height="52px" />
          <MyTaxApplicationCardInfoItem
            label="Dátum"
            value={dateOfDeclarationString}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Dolgozó neve"
            value={fullName}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Adóazonosító jele"
            value={taxNumber}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <MyTaxModifiedStatementCheck
            label={t("MY_TAX_MODIFIED_APPLICATION")}
            name="isModifiedDeclaration"
            value={states?.isModifiedDeclaration}
            onChange={handleModChange}
            disabled={!editable || isModified}
            isMobile={isMobile}
          />
          <Box height="36px" />
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            A bevétel adóelőlegének megállapításához kérem a súlyosan fogyatékos
            magánszemélyek személyi kedvezményének figyelembevételét.
          </Typography>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="h6" color={editable ? "primary" : "info"}>
              I.
            </Typography>
            <Box width="16px" />
            <Typography variant="h6" color={editable ? "primary" : "info"}>
              Orvosi igazolás alapján nyilatkozom
            </Typography>
          </Box>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              1.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A fogyatékos állapot kezdő napja
            </Typography>
          </Box>
          <Box height="16px" />
          <Box id="defectiveStart">
            <Box marginLeft="30px" width="200px">
              <OcDatePicker
                id="disabledStateFrom"
                label="Kezdő nap"
                value={
                  states?.disabledStateFrom
                    ? new Date(states.disabledStateFrom)
                    : null
                }
                onChange={(date) =>
                  handleChange({ key: "disabledStateFrom", value: date })
                }
                format={getDateFormatByLocale(i18n.language)}
                disabled={!editable}
              />
            </Box>
          </Box>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              2.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A fogyatékos állapot végső napja
            </Typography>
          </Box>
          <Box height="16px" />
          <Box id="defectiveEnd">
            <Box marginLeft="30px" width="200px">
              <OcDatePicker
                id="disabledStateTo"
                label="Végső nap"
                value={
                  states?.disabledStateTo
                    ? new Date(states.disabledStateTo)
                    : null
                }
                onChange={(date) =>
                  handleChange({ key: "disabledStateTo", value: date })
                }
                format={getDateFormatByLocale(i18n.language)}
                disabled={!editable}
              />
            </Box>
          </Box>
          <Box height="27px" />
          <MyTaxCreditApplicationCheckboxLine
            id="defectiveIsPermanent"
            type="checkbox"
            prefix="3."
            checked={states?.isFinal}
            onChange={(e) =>
              handleChange({ key: "isFinal", value: e.target.checked })
            }
            text="A fogyatékos állapot végleges"
            checkboxDisabled={!editable}
            editable={editable}
          />
          <Box height="18px" />
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography variant="h6" color={editable ? "primary" : "info"}>
              II.
            </Typography>
            <MyTaxCreditApplicationCheckboxLine
              id="receiveCrippledBenefit"
              type="checkbox"
              checked={states?.disabilityPension}
              checkboxDisabled={!editable}
              onChange={(e) =>
                handleChange({
                  key: "disabilityPension",
                  value: e.target.checked,
                })
              }
              editable={editable}
              text="Rokkantsági járadékban részesülök"
            />
          </Box>
          <Box height="27px" />
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              4.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A rokkantsági járadékot megállapító határozat száma
            </Typography>
          </Box>
          <Box height="16px" />
          <Box marginLeft="30px">
            <TextField
              id="crippledDecisionNumber"
              variant="outlined"
              label="Határozatszám"
              type="string"
              value={states?.disabilityPensionNumber}
              onChange={(e) =>
                handleChange({
                  key: "disabilityPensionNumber",
                  value: e.target.value,
                })
              }
              style={{ width: isMobile ? "100%" : "467px" }}
              disabled={!editable}
            />
          </Box>
          <Box height="27px" />
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography variant="h6" color={editable ? "primary" : "info"}>
              III.
            </Typography>
            <MyTaxCreditApplicationCheckboxLine
              id="receiveDefectiveSupport"
              type="checkbox"
              checked={states?.disabilitySupport}
              checkboxDisabled={!editable}
              onChange={(e) =>
                handleChange({
                  key: "disabilitySupport",
                  value: e.target.checked,
                })
              }
              editable={editable}
              text="Fogyatékossági támogatásban részesülök"
            />
          </Box>
          <Box height="27px" />
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              5.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A fogyatékossági támogatást megállapító határozat száma
            </Typography>
          </Box>
          <Box height="16px" />
          <Box marginLeft="30px">
            <TextField
              id="defectiveDecisionNumber"
              variant="outlined"
              label="Határozatszám"
              type="string"
              value={states?.disabilitySupportNumber}
              onChange={(e) =>
                handleChange({
                  key: "disabilitySupportNumber",
                  value: e.target.value,
                })
              }
              style={{ width: isMobile ? "100%" : "467px" }}
              disabled={!editable}
            />
          </Box>
          <Box height="27px" />
          <MyTaxCreditApplicationCheckboxLine
            id="applyToWithdrawal"
            type="checkbox"
            prefix="6."
            checked={states?.applyToWithdrawal}
            onChange={(e) =>
              handleChange({
                key: "applyToWithdrawal",
                value: e.target.checked,
              })
            }
            text="Nyilatkozatomat visszavonásig kérem figyelembe venni (folytatólagos nyilatkozatot teszek)."
            checkboxDisabled={!editable}
            editable={editable}
          />
          <Box height="18px" />
          <MyTaxCreditApplicationCheckboxLine
            id="noRequestRelief"
            type="checkbox"
            prefix="7."
            checked={states?.noRequestRelief}
            onChange={(e) =>
              handleChange({
                key: "noRequestRelief",
                value: e.target.checked,
              })
            }
            text="A nyilatkozattétel időpontjától kezdődően (erre a hónapra sem) a személyi kedvezményt nem kívánom igénybe venni"
            checkboxDisabled={!editable || !states?.isModifiedDeclaration}
            editable={editable}
          />
          <Box height="18px" />
          <Typography variant="h6" color={editable ? "primary" : "info"}>
            Nyilatkozat a külföldön érvényesíthető kedvezményről
          </Typography>
          <Box height="36px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              8.
            </Typography>
            <Box width="26px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Nyilatkozom, hogy jogosult vagyok a személyi kedvezményt
              Magyarországon érvényesíteni, külföldi államban a jövedelmem után
              azonos vagy hasonló kedvezményt nem veszek (vettem) igénybe.
            </Typography>
          </Box>
          <Box height="26px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={editable && taxApplicationSaveEnabled}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={handleSave}
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest}
          showModifiedButton={showModifiedButton}
          showRemoveButton={showRemoveButton}
          isModified={isModified}
          isModifiedDeclaration={states?.isModifiedDeclaration}
          isMobile={isMobile}
        />
      </Box>
    </>
  );
};

export default MyTaxMyPersonalTaxCreditApplication;
